/**
 * Longueurs maximales des champs de saisie — miroir des `@MaxLength` posés sur
 * les DTO de l'API (`pharmaworkspace-api/src/**\/dto`).
 *
 * A-12 (recette 2026-08-07) — la limite de description n'était nulle part côté
 * client : elle ne se découvrait qu'au refus du serveur, après une saisie
 * complète, et le message reçu était celui du validateur. Les borner ici permet
 * au navigateur d'arrêter la frappe au bon endroit et d'afficher un décompte.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RÈGLE D'ALIGNEMENT — à lire avant de toucher une valeur.
 *
 * Ces valeurs DOIVENT rester égales à celles de l'API. Ni plus basses : elles
 * interdiraient une saisie que le serveur accepte, et rendraient INMODIFIABLE
 * une fiche déjà enregistrée plus longue que la nouvelle borne — l'écran
 * refuserait de laisser corriger ce qu'il affiche. Ni plus hautes : elles
 * laisseraient repartir exactement l'erreur serveur qu'elles sont censées
 * éviter.
 *
 * Corollaire : ABAISSER une borne se fait des DEUX côtés, et seulement après
 * avoir vérifié qu'aucune ligne en base ne dépasse la nouvelle valeur.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Les constantes sont regroupées par NATURE de champ, pas par écran : deux
 * écrans qui saisissent la même chose (le nom d'un produit dans une commande et
 * dans une rupture) partagent la même borne, et une divergence saute aux yeux.
 */

// ── Textes courts identifiant une fiche ─────────────────────────────────────

/**
 * Intitulé d'une instance : titre de tâche, de publication, de contrôle
 * qualité, de non-conformité. (Les noms d'entités — produit, client, matériel,
 * société — ont leur propre borne, `MAX_NAME`.)
 *
 * 80 et non 200 (valeur d'origine) — recette 2026-08-20.
 *
 * À ne PAS relire comme un correctif de débordement : le débordement est réglé
 * par le CSS (`min-w-0` sur chaque maillon flex, cf. `check-text-overflow.cjs`),
 * et le resterait à 200. Cette borne-ci ne joue que sur la DENSITÉ : en vue
 * téléphone, la colonne de titre d'une carte de tâche non assignée fait environ
 * 110 px une fois retranchés la pastille de priorité et le bouton « Prendre en
 * charge », soit ~14 caractères par ligne. À 200, un titre maximal étalait la
 * carte sur une douzaine de lignes et noyait la liste ; à 80 il tient en 3 à 6
 * lignes selon l'encombrement de la droite.
 *
 * Pourquoi pas plus bas : un intitulé métier légitime atteint 70 caractères
 * (« Contrôle température réfrigérateur vaccins — relevé quotidien obligatoire »
 * en fait 73). Descendre à 60 refuserait des saisies valides, et rapprocherait
 * la borne des lignes déjà en base — qu'on ne peut pas relire d'ici, alors
 * qu'une fiche plus longue que la borne devient inmodifiable (cf. la RÈGLE
 * D'ALIGNEMENT en tête de fichier).
 *
 * MÊME borne en vue téléphone et en vue bureau, volontairement : une borne qui
 * varierait selon la largeur d'écran romprait dès qu'on modifie une fiche
 * depuis l'autre appareil.
 */
export const MAX_TITLE = 80;

/** Idem, pour tout ce qui nomme une entité (produit, client, matériel, société). */
export const MAX_NAME = 200;

/**
 * Nom ou prénom d'un patient, saisis séparément (ordonnances). Plus court qu'un
 * intitulé : c'est un nom de personne, pas une désignation.
 */
export const MAX_PERSON_NAME = 120;

/**
 * Moitié d'un nom composé stocké en UN seul champ côté API — le profil saisit
 * « Prénom » et « Nom » puis les recolle avec une espace.
 *
 * 99 et non 100 : deux moitiés pleines plus le séparateur font 199, donc sous
 * la borne de 200 du champ `name`. À 100 chacune, le total atteindrait 201 et le
 * serveur refuserait un formulaire que rien à l'écran n'avait signalé.
 */
export const MAX_NAME_PART = 99;

/** Étiquette libre courte : métier d'un contact, périodicité, créneau. */
export const MAX_SHORT_LABEL = 60;

/** Référence documentaire d'un contrôle ou d'une non-conformité (« PR-QUAL-012 »). */
export const MAX_REF = 40;

/** Intitulé d'un créneau de planning (« Ouverture », « Garde »). */
export const MAX_SLOT_LABEL = 40;

/** Libellé de créneau saisi côté planning (champ `slot`). */
export const MAX_SLOT = 60;

/** Nature d'un incident qualité (« Chaîne du froid »). */
export const MAX_INCIDENT_TYPE = 120;

/** Nom d'un canal de discussion. */
export const MAX_CHAT_NAME = 80;

/** N° de compte / référence client chez un fournisseur. */
export const MAX_ACCOUNT_REF = 100;

// ── Coordonnées ─────────────────────────────────────────────────────────────

/**
 * Téléphone — contact, client de location, officine. Une seule valeur pour les
 * trois, parce que l'API n'en connaît qu'une.
 *
 * ATTENTION EN RELISANT L'API : côté contacts et locations, cette borne ne
 * s'écrit PAS `@MaxLength(40)` sur le champ mais vit dans le décorateur
 * composite `@IsOptionalPhone()` (`common/dto/phone.decorator.ts`). Un
 * inventaire des `@MaxLength` par recherche textuelle ne la voit donc pas, et
 * conclut à tort que le champ hérite du 200 du champ précédent — c'est
 * exactement l'erreur qu'a rattrapée `check-limits.cjs`.
 */
export const MAX_PHONE = 40;

export const MAX_EMAIL = 255;
export const MAX_ADDRESS = 300;

/** Site web d'un contact. */
export const MAX_URL = 255;

/** Lien YouTube d'une publication — large, les URL de partage sont verbeuses. */
export const MAX_YOUTUBE_URL = 2048;

/** N° RPPS / FINESS de l'officine, champ libre des paramètres. */
export const MAX_RPPS = 60;

/**
 * N° FINESS du formulaire d'inscription : exactement 9 caractères, l'API le
 * valide par une expression régulière (`/^[0-9]{2}[0-9AB][0-9]{6}$/i`) et non par
 * une longueur. La borne de saisie est donc la longueur EXACTE attendue — taper
 * un dixième caractère ne peut produire qu'un refus.
 */
export const MAX_FINESS = 9;

// ── Codes ───────────────────────────────────────────────────────────────────

/** Code-barres CIP / EAN : 13 chiffres en pratique, 20 pour absorber les variantes. */
export const MAX_BARCODE = 20;

// ── Textes longs ────────────────────────────────────────────────────────────

/** Description d'une tâche ; notes d'une commande, location, rupture, contact, ordonnance. */
export const MAX_DESCRIPTION = 2000;

/** Motif d'une demande de congé ou d'absence. */
export const MAX_LEAVE_NOTE = 1000;

/** Note attachée à une ligne de planning. */
export const MAX_PLANNING_NOTE = 500;

/** Description d'un incident qualité. */
export const MAX_INCIDENT_DESCRIPTION = 4000;

/** Message de discussion. */
export const MAX_CHAT_MESSAGE = 4000;

/** Corps d'une publication d'actualité — le seul champ de rédaction longue. */
export const MAX_NEWS_BODY = 20000;

/** Motif de suspension communiqué par le super-admin. */
export const MAX_REJECTION_REASON = 1000;

// ── Recherche ───────────────────────────────────────────────────────────────

/**
 * Terme de recherche. Borné comme le reste : une requête de plusieurs milliers
 * de caractères ne cherche rien, elle fait travailler la base pour rien.
 */
export const MAX_SEARCH = 100;

// ── Valeurs numériques ──────────────────────────────────────────────────────

/** Quantité d'une ligne de commande fournisseur, exprimée en boîtes. */
export const MAX_ORDER_QTY = 100_000;

// ── Paramètres de l'officine ────────────────────────────────────────────────

/**
 * Bornes de l'écran « Paramètres », qui rend ses champs par une boucle : sans
 * table, tous auraient hérité de la même limite, la plus large — ce qui laisse
 * repartir l'erreur serveur sur les plus étroits.
 */
export const OFFICINE_FIELD_LIMITS = {
  name: MAX_NAME,
  address: MAX_ADDRESS,
  phone: MAX_PHONE,
  email: MAX_EMAIL,
  rpps: MAX_RPPS,
} as const;
